import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Loading, Modal, Pagination, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import { GET_ITEM_LIMIT } from "constants/constants"
import useCommon from "hooks/useCommon"
import { useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import {
  AssetCategoryRecord,
  useCreateAssetCategoryMutation,
  useDeleteAssetCategoryMutation,
  useListAssetCategoriesQuery,
  useUpdateAssetCategoryMutation,
} from "services/aimsBackend"

export default function AssetCategoryTab() {
  const { showSnackBar } = useCommon()
  const { data: items = [], isFetching } = useListAssetCategoriesQuery()
  const [createCategory, { isLoading: isCreating }] = useCreateAssetCategoryMutation()
  const [updateCategory, { isLoading: isUpdating }] = useUpdateAssetCategoryMutation()
  const [deleteCategory, { isLoading: isDeleting }] = useDeleteAssetCategoryMutation()

  const [search, setSearch] = useState("")
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)

  const [showAddModal, setShowAddModal] = useState(false)
  const [addTitle, setAddTitle] = useState("")
  const [addAbbreviation, setAddAbbreviation] = useState("")
  const [addError, setAddError] = useState<{ title?: string; abbreviation?: string }>({})

  const [editItem, setEditItem] = useState<AssetCategoryRecord | null>(null)
  const [editTitle, setEditTitle] = useState("")
  const [editAbbreviation, setEditAbbreviation] = useState("")
  const [editError, setEditError] = useState<{ title?: string; abbreviation?: string }>({})

  const [deleteTarget, setDeleteTarget] = useState<AssetCategoryRecord | null>(null)

  const filteredRows = items.filter(item => {
    const k = search.toLowerCase()
    return item.title.toLowerCase().includes(k) || item.abbreviation.toLowerCase().includes(k)
  })
  const pagedRows = filteredRows.slice(
    pageNumber === 1 ? 0 : (pageNumber - 1) * pageSize,
    (pageNumber - 1) * pageSize + pageSize,
  )

  const formatApiError = (err: any, fallback: string) => {
    const data = err?.data
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
      return Object.entries(data)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
        .join("; ")
    }
    return fallback
  }

  const handleAdd = async () => {
    const errs: typeof addError = {}
    if (!addTitle.trim()) errs.title = "Title is required"
    if (!addAbbreviation.trim()) errs.abbreviation = "Abbreviation is required"
    if (Object.keys(errs).length > 0) {
      setAddError(errs)
      return
    }
    try {
      await createCategory({ title: addTitle.trim(), abbreviation: addAbbreviation.trim() }).unwrap()
      showSnackBar("Asset category added successfully", "success")
      setAddTitle("")
      setAddAbbreviation("")
      setAddError({})
      setShowAddModal(false)
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to create asset category"), "error")
    }
  }

  const handleEdit = async () => {
    const errs: typeof editError = {}
    if (!editTitle.trim()) errs.title = "Title is required"
    if (!editAbbreviation.trim()) errs.abbreviation = "Abbreviation is required"
    if (Object.keys(errs).length > 0) {
      setEditError(errs)
      return
    }
    if (!editItem) return
    try {
      await updateCategory({
        id: editItem.id,
        body: { title: editTitle.trim(), abbreviation: editAbbreviation.trim() },
      }).unwrap()
      showSnackBar("Asset category updated successfully", "success")
      setEditItem(null)
      setEditTitle("")
      setEditAbbreviation("")
      setEditError({})
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to update asset category"), "error")
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteCategory(deleteTarget.id).unwrap()
      showSnackBar("Asset category deleted successfully", "success")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to delete asset category"), "error")
    } finally {
      setDeleteTarget(null)
    }
  }

  const columns: Array<ColumnShape<AssetCategoryRecord>> = [
    { key: "abbreviation", dataKey: "abbreviation", title: "Abbreviation", width: 150, flexGrow: 0 },
    { key: "title", dataKey: "title", title: "Title", width: 300, flexGrow: 1 },
    {
      key: "action",
      title: "",
      width: 260,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex">
          <Button
            action={() => {
              setEditItem(rowData)
              setEditTitle(rowData.title)
              setEditAbbreviation(rowData.abbreviation)
              setEditError({})
            }}
            label="Edit"
            icon={faPen}
            className="btn-full blue action-btn"
          />
          <Button
            action={() => setDeleteTarget(rowData)}
            label="Delete"
            icon={faTrash}
            className="btn-full danger action-btn ml-2"
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="mt-3">
        <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
          <Loading active={isFetching || isDeleting} />
          <div style={{ flex: 1, maxWidth: 400 }}>
            <TextInput
              labelText="Search"
              hideLabel
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPageNumber(1)
              }}
              placeholder="Search asset categories..."
            />
          </div>
          <Button
            action={() => {
              setAddTitle("")
              setAddAbbreviation("")
              setAddError({})
              setShowAddModal(true)
            }}
            label="Add asset category"
            className="btn-full green"
            icon={faPlus}
          />
        </div>

        <div style={{ height: "100%", minHeight: "60vh", marginTop: 10 }}>
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={pagedRows}
                width={width}
                height={height}
                rowKey="id"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        </div>

        <Pagination
          totalItems={filteredRows.length}
          pageSize={pageSize}
          pageSizes={[10, 20, 30]}
          page={pageNumber}
          onChange={(event: any): void => {
            setPageNumber((event as any).page)
            setPageSize((event as any).pageSize)
          }}
        />
      </div>

      <Modal
        open={showAddModal}
        modalHeading="Add Asset Category"
        primaryButtonText={isCreating ? "Adding..." : "Add"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowAddModal(false)}
        onSecondarySubmit={() => setShowAddModal(false)}
        onRequestSubmit={handleAdd}
        primaryButtonDisabled={isCreating || !addAbbreviation.trim() || !addTitle.trim()}
      >
        <div className="mb-3">
          <TextInput
            labelText="Abbreviation *"
            value={addAbbreviation}
            onChange={e => {
              setAddAbbreviation(e.target.value)
              if (addError.abbreviation) setAddError(prev => ({ ...prev, abbreviation: undefined }))
            }}
            placeholder="e.g. Bridge"
            maxLength={64}
            invalid={!!addError.abbreviation}
            invalidText={addError.abbreviation}
          />
        </div>
        <TextInput
          labelText="Title *"
          value={addTitle}
          onChange={e => {
            setAddTitle(e.target.value)
            if (addError.title) setAddError(prev => ({ ...prev, title: undefined }))
          }}
          placeholder="Enter asset category title"
          maxLength={255}
          invalid={!!addError.title}
          invalidText={addError.title}
        />
      </Modal>

      <Modal
        open={!!editItem}
        modalHeading="Edit Asset Category"
        primaryButtonText={isUpdating ? "Saving..." : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setEditItem(null)}
        onSecondarySubmit={() => setEditItem(null)}
        onRequestSubmit={handleEdit}
        primaryButtonDisabled={isUpdating || !editAbbreviation.trim() || !editTitle.trim()}
      >
        <div className="mb-3">
          <TextInput
            labelText="Abbreviation *"
            value={editAbbreviation}
            onChange={e => {
              setEditAbbreviation(e.target.value)
              if (editError.abbreviation) setEditError(prev => ({ ...prev, abbreviation: undefined }))
            }}
            placeholder="e.g. Bridge"
            maxLength={64}
            invalid={!!editError.abbreviation}
            invalidText={editError.abbreviation}
          />
        </div>
        <TextInput
          labelText="Title *"
          value={editTitle}
          onChange={e => {
            setEditTitle(e.target.value)
            if (editError.title) setEditError(prev => ({ ...prev, title: undefined }))
          }}
          placeholder="Enter asset category title"
          maxLength={255}
          invalid={!!editError.title}
          invalidText={editError.title}
        />
      </Modal>

      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
      >
        Are you sure you want to delete &quot;{deleteTarget?.title}&quot;?
      </DeleteConfirmationModal>
    </>
  )
}
